/** Form for creating and editing catalog products (external medication entries). */
import z from 'zod'

import { pluralizeUnit } from '@snootsvet/shared'

import { useAppForm } from '@/frontend/components/form'
import { Button } from '@/frontend/components/ui/button'
import { useCreateCatalogProduct } from '@/frontend/features/inventory/use-create-catalog-product'
import { useUpdateCatalogProduct } from '@/frontend/features/inventory/use-update-catalog-product'

const catalogProductSchema = z.object({
  name: z.string().min(1, 'Medication name is required'),
  formulation: z.string().min(1, 'Formulation is required'),
  unit: z.string(),
  isControlledSubstance: z.boolean(),
})

export type CatalogProductFormValues = z.infer<typeof catalogProductSchema>

type CatalogProductFormProps = {
  defaultValues?: Partial<CatalogProductFormValues>
  /** When provided, the form operates in edit mode and PATCHes the existing record. */
  catalogProductId?: string
  onSuccess?: (productId: string) => void
  onCancel?: () => void
}

/**
 * Form for adding or editing a catalog product.
 * When `catalogProductId` is provided the form PATCHes the existing record;
 * otherwise it POSTs a new one.
 */
export function CatalogProductForm({
  defaultValues,
  catalogProductId,
  onSuccess,
  onCancel,
}: CatalogProductFormProps) {
  const { mutateAsync: createProduct } = useCreateCatalogProduct()
  const { mutateAsync: updateProduct } = useUpdateCatalogProduct()
  const isEditing = catalogProductId !== undefined

  const form = useAppForm({
    defaultValues: {
      name: '',
      formulation: '',
      unit: '',
      isControlledSubstance: false,
      ...defaultValues,
    },
    validators: {
      onSubmit: catalogProductSchema,
    },
    onSubmit: async ({ value, formApi }) => {
      try {
        const trimmedUnit = value.unit.trim()
        if (isEditing) {
          const product = await updateProduct({
            id: catalogProductId,
            name: value.name.trim(),
            formulation: value.formulation.trim(),
            unit: trimmedUnit || null,
            isControlledSubstance: value.isControlledSubstance,
          })
          onSuccess?.(product.id)
        } else {
          const product = await createProduct({
            name: value.name.trim(),
            formulation: value.formulation.trim(),
            unit: trimmedUnit || undefined,
            isControlledSubstance: value.isControlledSubstance,
          })
          onSuccess?.(product.id)
        }
      } catch (err) {
        formApi.setErrorMap({
          onSubmit: {
            form: err instanceof Error ? err.message : 'Failed to save product',
            fields: {},
          },
        })
      }
    },
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <div className="flex flex-col gap-4 py-2">
          <form.GlobalError />

          <p className="text-sm text-muted-foreground">
            Add a medication that doesn't exist in the external prescription product catalog yet.
            For example:
          </p>
          <ul className="text-sm text-muted-foreground list-disc pl-4 -mt-2 space-y-0.5">
            <li>
              <strong>Name:</strong> Metacam &nbsp; <strong>Formulation:</strong> 1.5mg/ml oral
              suspension &nbsp; <strong>Unit:</strong> ml
            </li>
            <li>
              <strong>Name:</strong> Synulox &nbsp; <strong>Formulation:</strong> 250mg film-coated
              tablets &nbsp; <strong>Unit:</strong> tablet
            </li>
          </ul>

          <form.AppField name="name">
            {(field) => (
              <field.InputField label="Medication Name" placeholder="e.g. Metacam" required />
            )}
          </form.AppField>

          <div className="grid grid-cols-2 gap-4">
            <form.AppField name="formulation">
              {(field) => (
                <field.InputField
                  label="Formulation"
                  placeholder="e.g. 1.5mg/ml oral suspension"
                  required
                />
              )}
            </form.AppField>

            <form.AppField name="unit">
              {(field) => <field.InputField label="Unit" placeholder="e.g. ml" />}
            </form.AppField>
          </div>

          <form.AppField name="isControlledSubstance">
            {(field) => (
              <field.CheckboxField
                label="Controlled substance"
                description="Controlled drug - requires a wet signature (no e-signing)"
              />
            )}
          </form.AppField>

          {/* Live preview mirroring the written prescription PDF layout */}
          <form.Subscribe selector={(s) => s.values}>
            {(values) => {
              const hasContent = values.name || values.formulation
              if (!hasContent) return null
              return (
                <div className="rounded-md border border-border bg-muted/40 px-3 py-2.5 space-y-0.5">
                  <p className="text-2xs font-medium text-muted-foreground mb-1.5">
                    How this product would appear on a written prescription
                  </p>
                  <p className="text-sm">
                    <span className="font-semibold">Drug Name & Strength: </span>
                    {values.name || (
                      <span className="italic text-muted-foreground">e.g. Metacam</span>
                    )}
                  </p>
                  {values.formulation && (
                    <p className="text-sm">
                      <span className="font-semibold">Formulation: </span>
                      {values.formulation}
                    </p>
                  )}
                  <p className="text-sm">
                    <span className="font-semibold">Total Quantity: </span>
                    <span className="text-muted-foreground">
                      10 (TEN) {pluralizeUnit(values.unit || null, 10)}
                    </span>
                  </p>
                </div>
              )
            }}
          </form.Subscribe>
        </div>

        <div className="flex items-center justify-end gap-x-2 pt-3">
          {onCancel && (
            <Button variant="outline" size="lg" type="button" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <form.SubmitButton>{isEditing ? 'Save Changes' : 'Create Medication'}</form.SubmitButton>
        </div>
      </form.AppForm>
    </form>
  )
}
